import React, { useEffect, useMemo } from "react";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Autocomplete, Divider, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook } from "hooks";
import { useNavigate } from "hooks/shared-hooks/use-navigate";
import useDeals from "hooks/tagger-hooks/useDeals";
import useFolders from "hooks/tagger-hooks/useFolders";
import { useDealActions, useTaggerActions, useTaggerDocumentArraySelector, useTaggerSelector } from "store";

import { DocumentFileDataField, fundFilesTypes } from "../../../constants";

const DocumentTopBar = ({
  docId,
  currentDocIndex,
  setCurrentDocIndex
}: {
  docId: number;
  currentDocIndex: number;
  setCurrentDocIndex: (index: number) => void;
}) => {
  const { options: dealOptions, value: dealValue } = useDeals(docId);
  const { value: folder } = useFolders({ docId });
  const navigate = useNavigate();
  const documents = useTaggerDocumentArraySelector();
  const taggerState = useTaggerSelector();
  const { changeDocumentFileData } = useTaggerActions();
  const { getDealInvestors } = useDealActions();

  const {
    action: getDealInvestorsAction,
    data: dealInvestors,
    loading: dealInvestorsLoading
  } = useActionHook(getDealInvestors);

  // const { options: roundOptions, value: roundValue, onChange: onRoundChange } = useRounds(docId);
  // const { value: dateValue, onChange: onDateChange } = useDate(docId);
  // const { value: currencyValue, options: currOptions, onChange: onCurrencyChange } = useCurrency(docId);
  // const {
  //   loading: invLoading,
  //   options: invOptions,
  //   value: invValue,
  //   onChange: onInvChange
  // } = useInvestors(docId, roundValue!);

  // const { search } = useLocation();
  // const hasDealInUrl = new URLSearchParams(search).has("dealId");
  const currentFileData = useMemo(() => documents[currentDocIndex]?.fileData, [documents, currentDocIndex]);
  const dealId = useMemo(() => documents[currentDocIndex]?.dealId, [documents, currentDocIndex]);

  const onChangeFileData = (docId: number, field: DocumentFileDataField, value: string | number | null) => {
    changeDocumentFileData(docId, field, value);
  };

  const documentOptions = useMemo(
    () =>
      documents.map((doc, index) => ({
        label: doc.file.name,
        id: taggerState.documentIds[index],
        index
      })),
    [documents, taggerState.documentIds]
  );

  const dealName = useMemo(
    () => dealOptions.find((deal) => deal.value === dealValue)?.label || "",
    [dealOptions, dealValue]
  );

  const handlePrevious = () => {
    if (currentDocIndex > 0) {
      setCurrentDocIndex(currentDocIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentDocIndex < documents.length - 1) {
      setCurrentDocIndex(currentDocIndex + 1);
    }
  };

  const handleDownloadFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const elem = document.createElement("a");
    elem.href = url;
    elem.download = file.name;
    elem.click();
    URL.revokeObjectURL(elem.href);
  };

  useEffect(() => {
    if (dealId) {
      getDealInvestorsAction({ dealId: dealId });
    }
  }, [dealId]);

  return (
    <Paper sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" sx={{ width: "100%", padding: "16px" }}>
        <Stack direction="row" alignItems="center" gap="50px">
          <Stack flexDirection="column" minHeight="61px" gap="10px" minWidth="120px">
            <Typography fontWeight="500" fontSize="14px">
              Deal
            </Typography>
            <Typography
              fontSize="14px"
              sx={{ cursor: "pointer", color: "#26A6AE" }}
              onClick={() => dealValue && navigate({ route: "deal", params: { deal_id: dealValue } })}
            >
              {dealName}
            </Typography>
          </Stack>
          <Divider sx={{ width: "1px", height: "50px", backgroundColor: "#DBE8F5", borderColor: "#DBE8F5" }} />
          <Stack flexDirection="column" minHeight="61px" gap="10px" minWidth="120px">
            <Typography fontWeight="500" fontSize="14px">
              Folder
            </Typography>
            <Typography
              fontSize="14px"
              sx={{ cursor: "pointer", color: "#26A6AE" }}
              onClick={() =>
                dealValue &&
                folder &&
                navigate({
                  route: "dealFolder",
                  params: { deal_id: dealValue, folder_id: folder.id },
                  search: { section: folder.id }
                })
              }
            >
              {folder?.title}
            </Typography>
          </Stack>
          <Divider sx={{ width: "1px", height: "50px", backgroundColor: "#DBE8F5", borderColor: "#DBE8F5" }} />
          <Stack flexDirection="column" minHeight="61px" gap="10px" minWidth="120px">
            <Typography fontWeight="500" fontSize="14px">
              File Type
            </Typography>
            <Autocomplete
              sx={{ minWidth: 200 }}
              options={fundFilesTypes}
              disabled={!currentFileData}
              value={currentFileData?.document_type ?? null}
              onChange={(_, option) => {
                onChangeFileData(docId, DocumentFileDataField.DocumentType, option);
                if (!option) {
                  onChangeFileData(docId, DocumentFileDataField.InvestorId, null);
                }
              }}
              renderInput={(params) => <TextField {...params} placeholder="Select Document Type" />}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option}>
                    {option}
                  </li>
                );
              }}
            />
          </Stack>
          <Stack flexDirection="column" minHeight="61px" gap="10px" minWidth="120px">
            <Typography fontWeight="500" fontSize="14px">
              Investor
            </Typography>
            <Stack direction="row" alignItems="center" gap="16px">
              <InvestorAutocomplete
                key={currentFileData?.document_type}
                disabled={Boolean(!currentFileData?.document_type)}
                sx={{ minWidth: 200 }}
                label=""
                loading={dealInvestorsLoading}
                dealInvestors={dealInvestors || []}
                preselectedInvestorId={currentFileData?.investor_id ?? null}
                onChange={(value) => onChangeFileData(docId, DocumentFileDataField.InvestorId, value?.id || null)}
              />
              {Boolean(!currentFileData?.document_type) && (
                <MaterialTooltip title={"File-type must be selected"} isShow>
                  <InfoOutlinedIcon sx={{ fontSize: "20px" }} />
                </MaterialTooltip>
              )}
            </Stack>
          </Stack>
          {documents.length > 1 && (
            <>
              <Divider sx={{ width: "1px", height: "50px", backgroundColor: "#DBE8F5", borderColor: "#DBE8F5" }} />
              <Stack direction="row" alignItems="center" sx={{ gap: "10px" }}>
                <Stack direction="column" alignItems="flex-start" sx={{ minWidth: "250px" }}>
                  <Typography fontWeight="500" fontSize="14px" sx={{ mb: "4px" }}>
                    File
                  </Typography>
                  <Stack flexDirection="row" alignItems="center" sx={{ gap: "10px" }}>
                    <Autocomplete
                      disableClearable
                      options={documentOptions}
                      value={documentOptions[currentDocIndex]}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          setCurrentDocIndex(newValue.index);
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          padding: "4px !important",
                          paddingRight: "32px !important"
                        },
                        "& .MuiAutocomplete-input": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }
                      }}
                      renderInput={(params) => (
                        <TextField sx={{ width: "200px" }} {...params} variant="outlined" size="small" />
                      )}
                    />
                    <Stack flexDirection="row" gap="20px">
                      <IconButton
                        onClick={handleNext}
                        disabled={currentDocIndex === documents.length - 1}
                        sx={{ padding: "2px" }}
                      >
                        <KeyboardArrowDownIcon fontSize="sm" />
                      </IconButton>
                      <IconButton onClick={handlePrevious} disabled={!currentDocIndex} sx={{ padding: "2px" }}>
                        <KeyboardArrowUpIcon fontSize="sm" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>

        {Boolean(documents?.[currentDocIndex]?.file) && (
          <MaterialTooltip title="Download file" isShow>
            <IconButton onClick={() => handleDownloadFile(documents?.[currentDocIndex]?.file)}>
              <FileDownloadOutlinedIcon />
            </IconButton>
          </MaterialTooltip>
        )}
      </Stack>
    </Paper>
  );
};

export default DocumentTopBar;
